/**
 * ChatGapChip — three variants of the data-gap confirmation UI.
 *
 * PHASE_D_GAP_QA_BRIEFING.md §4.6 (briefing supersedes
 * PHASE_D_ORCHESTRATION_AND_GAPS.md on the architecture: rendered when the
 * backend emits a `gap_q` SSE event from the request_missing_information
 * tool). The user's confirmation flows back as a regular user message —
 * the gap value is NOT written to the DB in V1.
 *
 * Variants:
 *   1. blocking + suggestion → "✓ Yes, {suggestion}" / "✗ Different {field}"
 *   2. blocking + no suggestion → most-common-value chips + "Other…"
 *   3. degrade (blocking=false) → single neutral "Tell me the {field}" chip
 */
import { useState } from 'react';

import { useChatTelemetry } from '../hooks/useChatTelemetry';
import { ChatGapInput } from './ChatGapInput';

interface Props {
  field: string;
  suggestion: string | null;
  source: string | null;
  blocking: boolean;
  prose: string;
  /** Called when the user accepts a value (suggested or chosen). The text
   * argument becomes the next user message, which resumes the original
   * question with the field now in conversational context. */
  onConfirm: (value: string) => void;
}

/** Most-common values per field for variant 2 (open-ended blocking gap).
 * Keep this small — five values is enough; the "Other…" fallback covers
 * the long tail and matches the brand-book "no spinner-loading" vibe. */
const COMMON_VALUES_BY_FIELD: Record<string, string[]> = {
  industry: ['Manufacturing', 'Retail', 'Technology', 'Services', 'Construction'],
};

function humanizeField(field: string): string {
  return field.replace(/_/g, ' ');
}

export function ChatGapChip({ field, suggestion, source, blocking, prose, onConfirm }: Props) {
  const [showInput, setShowInput] = useState(false);
  const { logEvent } = useChatTelemetry();

  // Wrap onConfirm so every accepted-value path emits the telemetry
  // event with whether the suggestion was accepted as-is or replaced.
  const handleConfirm = (value: string, acceptedSuggestion: boolean) => {
    logEvent('gap_q_resolved', {
      field,
      blocking,
      accepted_suggestion: acceptedSuggestion,
      source: source ?? null,
    });
    onConfirm(value);
  };

  // ── Variant 1: blocking with suggestion ────────────────────────────────
  if (blocking && suggestion) {
    if (showInput) {
      return (
        <ChatGapInput
          field={field}
          onSubmit={(value) => {
            setShowInput(false);
            handleConfirm(value, false);
          }}
          onCancel={() => setShowInput(false)}
        />
      );
    }
    return (
      <div className="mt-2 flex flex-col gap-2">
        {prose && (
          <p className="text-body-sm text-txt-secondary">{prose}</p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleConfirm(`Yes, ${suggestion}`, true)}
            className="border border-orange bg-surface-card px-3 py-1 text-body-sm text-orange transition-colors hover:bg-orange hover:text-surface-card"
          >
            {`✓ Yes, ${suggestion}`}
          </button>
          <button
            type="button"
            onClick={() => setShowInput(true)}
            className="border border-border bg-surface-card px-3 py-1 text-body-sm text-txt-secondary transition-colors hover:border-border-strong hover:text-txt-primary"
          >
            {`✗ Different ${humanizeField(field)}`}
          </button>
        </div>
        {source && (
          <p className="text-caption text-txt-tertiary">Inferred from {source.replace(/_/g, ' ')}.</p>
        )}
      </div>
    );
  }

  // ── Variant 2: blocking, no suggestion ─────────────────────────────────
  if (blocking && !suggestion) {
    if (showInput) {
      return (
        <ChatGapInput
          field={field}
          onSubmit={(value) => {
            setShowInput(false);
            handleConfirm(value, false);
          }}
          onCancel={() => setShowInput(false)}
        />
      );
    }
    const commonValues = COMMON_VALUES_BY_FIELD[field] ?? [];
    return (
      <div className="mt-2 flex flex-col gap-2">
        {prose && (
          <p className="text-body-sm text-txt-secondary">{prose}</p>
        )}
        <div className="flex flex-wrap gap-2">
          {commonValues.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => handleConfirm(v, false)}
              className="border border-border bg-surface-card px-3 py-1 text-body-sm text-txt-secondary transition-colors hover:border-orange hover:text-orange"
            >
              {v}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowInput(true)}
            className="border border-border bg-surface-card px-3 py-1 text-body-sm text-txt-secondary transition-colors hover:border-border-strong hover:text-txt-primary"
          >
            Other…
          </button>
        </div>
      </div>
    );
  }

  // ── Variant 3: degrade (blocking=false) ────────────────────────────────
  if (showInput) {
    return (
      <ChatGapInput
        field={field}
        onSubmit={(value) => {
          setShowInput(false);
          handleConfirm(value, false);
        }}
        onCancel={() => setShowInput(false)}
      />
    );
  }
  return (
    <div className="mt-2 flex flex-col gap-2">
      {prose && (
        <p className="text-body-sm text-txt-secondary">{prose}</p>
      )}
      <div>
        <button
          type="button"
          onClick={() => setShowInput(true)}
          className="border border-border bg-surface-card px-3 py-1 text-body-sm text-txt-secondary transition-colors hover:border-orange hover:text-orange"
        >
          Tell me the {humanizeField(field)}
        </button>
      </div>
    </div>
  );
}
